import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Loader2, Flame, ExternalLink, Copy, Check, Newspaper, Users, TrendingUp, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';

/** Un contenuto di un competitor, con lo script già trascritto. */
interface Contenuto {
  competitor_handle: string;
  competitor_name: string;
  follower: number;
  permalink: string;
  cover_url: string | null;
  likes: number;
  comments: number;
  caption: string | null;
  transcript: string | null;
  performance?: string | null;
  /** Nome storico della colonna: nasce quando la funzione era solo per un cliente. */
  spunto_alessandra?: string | null;
  posted_at?: string | null;
}

interface Competitor {
  handle: string;
  nome: string;
  follower: number;
  contenuti: Contenuto[];
}

/** Segnalazione del giro automatico del mattino: news o picco di un competitor. */
interface Alert {
  id: string;
  alert_date: string;
  tipo: string;
  rilevanza: string;
  titolo: string;
  descrizione: string | null;
  spunto: string | null;
  fonte: string | null;
}

const GIALLO = '#fdf751';

const migliaia = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}K` : String(n ?? 0));

const ETICHETTA_TIPO: Record<string, string> = {
  news: 'News di settore',
  picco: 'Contenuto virale di un competitor',
  post: 'Nuovo post di un competitor',
};

/** Data leggibile: oggi / ieri / "ven 6 giu". */
const quando = (s: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  const oggi = new Date();
  const altro = new Date(s);
  oggi.setHours(0, 0, 0, 0);
  altro.setHours(0, 0, 0, 0);
  const giorni = Math.round((oggi.getTime() - altro.getTime()) / 86400000);
  if (giorni === 0) return 'Oggi';
  if (giorni === 1) return 'Ieri';
  if (giorni > 1 && giorni < 7) return `${giorni} giorni fa`;
  return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
};

/**
 * Le descrizioni che arrivano dal giro automatico contengono link in markdown
 * ("([testata](https://…?utm_source=openai))"): stampati così sono una riga di
 * rumore in mezzo al testo. Qui resta il nome della fonte, l'indirizzo vero sta
 * già nel link "Leggi la fonte" sotto la scheda.
 */
const pulisci = (t: string): string =>
  t
    .replace(/\(\s*\[([^\]]+)\]\(\s*https?:\/\/[^)]+\)\s*\)/g, '($1)')
    .replace(/\[([^\]]+)\]\(\s*https?:\/\/[^)]+\)/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** Quanto conta: dice se vale la pena farci un contenuto adesso. */
const priorita = (a: Alert): { label: string; calda: boolean; spenta?: boolean } => {
  if (a.tipo === 'picco' || a.rilevanza === 'alta') {
    return { label: 'Da fare subito · argomento caldo', calda: true };
  }
  if (a.rilevanza === 'bassa') return { label: 'Solo contesto', calda: false, spenta: true };
  return { label: 'Da valutare', calda: false };
};

/**
 * Script del reel: in scheda se ne leggono le prime righe, il resto si apre
 * con un click. Le trascrizioni intere renderebbero la colonna un muro di testo.
 */
const TestoEspandibile: React.FC<{ testo: string; righe: number }> = ({ testo, righe }) => {
  const [aperto, setAperto] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);
  const [troncato, setTroncato] = useState(false);

  useLayoutEffect(() => {
    if (aperto) return;
    const misura = () => {
      const el = ref.current;
      if (el) setTroncato(el.scrollHeight > el.clientHeight + 1);
    };
    misura();
    window.addEventListener('resize', misura);
    return () => window.removeEventListener('resize', misura);
  }, [aperto, testo]);

  return (
    <>
      <p
        ref={ref}
        className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-200 whitespace-pre-line"
        style={
          aperto
            ? undefined
            : { display: '-webkit-box', WebkitLineClamp: righe, WebkitBoxOrient: 'vertical', overflow: 'hidden' }
        }
      >
        {testo}
      </p>
      {(troncato || aperto) && (
        <button
          type="button"
          onClick={() => setAperto((v) => !v)}
          className="mt-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-100"
        >
          {aperto ? 'Mostra meno' : 'Leggi tutto lo script'}
        </button>
      )}
    </>
  );
};

/** Lo spunto, con il pulsante per portarselo via nella chat dell'assistente. */
const Spunto: React.FC<{ etichetta: string; testo: string }> = ({ etichetta, testo }) => {
  const [copiato, setCopiato] = useState(false);
  const copia = () => {
    navigator.clipboard.writeText(testo).then(
      () => {
        setCopiato(true);
        toast.success('Spunto copiato: incollalo nell\'AI Assistant');
        setTimeout(() => setCopiato(false), 2000);
      },
      () => toast.error('Copia non riuscita')
    );
  };

  return (
    <div className="mt-2.5 flex items-start gap-2 rounded-lg bg-[#fdf751]/15 border border-[#fdf751]/50 px-2.5 py-2">
      <span className="text-slate-500 dark:text-slate-400 mt-0.5">→</span>
      <p className="flex-1 min-w-0 text-[13px] leading-relaxed text-slate-800 dark:text-slate-100">
        <span className="font-semibold">{etichetta} </span>
        {testo}
      </p>
      <button
        onClick={copia}
        title="Copia lo spunto"
        className="flex-shrink-0 p-1 rounded text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"
      >
        {copiato ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
};

/** Riquadro per una colonna ancora vuota: dice anche come si riempie. */
const Vuoto: React.FC<{ testo: string; comeSiRiempie: string }> = ({ testo, comeSiRiempie }) => (
  <div className="rounded-xl border border-dashed border-slate-300 dark:border-gray-600 bg-slate-50/60 dark:bg-gray-800/40 p-5">
    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{testo}</p>
    <p className="mt-2 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">{comeSiRiempie}</p>
  </div>
);

interface Props {
  clientId: string;
  clientName: string;
}

/**
 * News & Competitor, vista dal team.
 *
 * È lo stesso materiale che il cliente vede nella sua pagina condivisa, ma qui
 * serve a un'altra cosa: decidere cosa scrivere. Sta accanto all'AI Assistant
 * apposta, così chi scrive legge la segnalazione e passa subito a produrre il
 * contenuto senza cambiare pagina.
 *
 * I dati arrivano dal giro automatico del mattino: le fonti da monitorare si
 * impostano nella scheda del cliente, tab "News & Competitor".
 */
const NewsCompetitor: React.FC<Props> = ({ clientId, clientName }) => {
  const [inCaricamento, setInCaricamento] = useState(true);
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);

  const carica = React.useCallback(async (silenzioso = false) => {
    if (!silenzioso) setInCaricamento(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/competitors/public`);
      const dati = await res.json();
      if (dati.success) {
        setCompetitors(dati.data.competitors || []);
        setAlerts(dati.data.alerts || []);
      }
    } catch {
      toast.error('Non riesco a caricare news e competitor');
    } finally {
      setInCaricamento(false);
    }
  }, [clientId]);

  useEffect(() => {
    carica();
  }, [carica]);

  if (inCaricamento) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
        <p className="mt-2 text-sm text-slate-500">Carico news e competitor di {clientName}…</p>
      </div>
    );
  }

  // Prima le segnalazioni calde, poi le più recenti.
  const ordinate = [...alerts].sort((a, b) => {
    const ca = priorita(a).calda ? 1 : 0;
    const cb = priorita(b).calda ? 1 : 0;
    if (ca !== cb) return cb - ca;
    return new Date(b.alert_date).getTime() - new Date(a.alert_date).getTime();
  });

  const calde = ordinate.filter((a) => priorita(a).calda).length;
  const contenutiTotali = competitors.reduce((n, c) => n + c.contenuti.length, 0);
  const virali = competitors.reduce(
    (n, c) => n + c.contenuti.filter((x) => x.performance === 'virale').length,
    0
  );

  return (
    <div className="space-y-5">
      {/* Quanto materiale c'è, in numeri: dice subito se vale la pena guardare */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-0">
          {[
            { icona: Flame, etichetta: 'Segnalazioni calde', valore: calde },
            { icona: Newspaper, etichetta: 'Segnalazioni totali', valore: alerts.length },
            { icona: Users, etichetta: 'Competitor seguiti', valore: competitors.length },
            { icona: TrendingUp, etichetta: 'Loro contenuti virali', valore: virali },
          ].map((v) => (
            <div
              key={v.etichetta}
              className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2"
            >
              <div className="flex items-center gap-1.5">
                <v.icona size={13} className="text-slate-400" />
                <span className="text-lg font-bold text-slate-900 dark:text-slate-100 leading-none">
                  {v.valore}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{v.etichetta}</div>
            </div>
          ))}
        </div>
        <button
          onClick={() => carica(true)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:border-[#fdf751] hover:bg-[#fdf751]/10 transition-colors"
        >
          <RefreshCw size={13} /> Aggiorna
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        {/* Colonna 1: su cosa conviene scrivere adesso */}
        <section>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Su cosa conviene fare un contenuto
          </h3>
          <p className="mt-1 mb-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Il controllo gira ogni mattina sulle fonti impostate per {clientName}. Le segnalazioni
            in giallo sono argomenti caldi da coprire adesso; le altre sono da valutare o servono
            solo come contesto.
          </p>

          {ordinate.length === 0 ? (
            <Vuoto
              testo="Nessuna segnalazione al momento."
              comeSiRiempie={`Il giro automatico gira ogni mattina presto: se resta vuoto a lungo, controlla le fonti nella scheda di ${clientName}, tab «News & Competitor».`}
            />
          ) : (
            <div className="space-y-3">
              {ordinate.map((a) => {
                const p = priorita(a);
                return (
                  <article
                    key={a.id}
                    className={`rounded-xl border p-4 ${
                      p.calda
                        ? 'border-[#fdf751] bg-[#fdf751]/10'
                        : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    } ${p.spenta ? 'opacity-70' : ''}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      {p.calda ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-slate-900"
                          style={{ background: GIALLO }}
                        >
                          <Flame size={10} /> {p.label}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border border-slate-300 dark:border-gray-600 text-slate-500 dark:text-slate-400">
                          {p.label}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        {ETICHETTA_TIPO[a.tipo] || a.tipo}
                      </span>
                      <span className="text-[11px] text-slate-400">·</span>
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        {quando(a.alert_date)}
                      </span>
                    </div>

                    <p className="text-sm font-semibold leading-snug text-slate-900 dark:text-slate-100">
                      {pulisci(a.titolo)}
                    </p>
                    {a.descrizione && (
                      <p className="mt-1 text-[13px] leading-relaxed text-slate-600 dark:text-slate-300">
                        {pulisci(a.descrizione)}
                      </p>
                    )}
                    {a.spunto && <Spunto etichetta="Idea per il contenuto:" testo={pulisci(a.spunto)} />}
                    {a.fonte && (
                      <a
                        href={a.fonte}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-sky-600 break-all"
                      >
                        <ExternalLink size={10} className="flex-shrink-0" /> Leggi la fonte
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Colonna 2: cosa stanno pubblicando i competitor */}
        <section>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Cosa pubblicano i competitor
          </h3>
          <p className="mt-1 mb-4 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            Per ogni competitor i 4 reel pubblicati nell'ultimo mese con più like e commenti, con lo script (parlato trascritto o caption) e lo spunto per riproporli. Si aggiorna ogni mattina.
            Il badge <span className="font-semibold text-slate-700 dark:text-slate-200">Virale</span>{' '}
            segna quelli che stanno andando molto sopra la loro media.
          </p>

          {competitors.length === 0 ? (
            <Vuoto
              testo={`Non stiamo seguendo nessun competitor per ${clientName}.`}
              comeSiRiempie={`I profili da monitorare si aggiungono nella scheda del cliente, tab «News & Competitor»: da lì il giro automatico comincia a raccogliere i loro contenuti e a trascriverli.`}
            />
          ) : (
            <div className="space-y-6">
              {competitors.map((c) => (
                <div key={c.handle}>
                  <div className="flex items-baseline gap-2 mb-2.5">
                    <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {c.nome}
                    </span>
                    <span className="text-[11px] text-slate-400">
                      {c.handle} · {migliaia(c.follower)} follower
                    </span>
                  </div>

                  <div className="space-y-3">
                    {c.contenuti.map((ct) => (
                      <article
                        key={ct.permalink}
                        className="flex gap-3 rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
                      >
                        <a
                          href={ct.permalink}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 block relative rounded-lg overflow-hidden bg-slate-100 dark:bg-gray-700"
                          style={{ width: 96, aspectRatio: '9/16' }}
                          title="Apri su Instagram"
                        >
                          {ct.cover_url && (
                            <img src={ct.cover_url} alt="" className="w-full h-full object-cover" />
                          )}
                          {ct.performance === 'virale' && (
                            <span
                              className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full text-slate-900"
                              style={{ background: GIALLO }}
                            >
                              <Flame size={9} /> Virale
                            </span>
                          )}
                        </a>

                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] mb-2">
                            <span className="font-semibold text-slate-800 dark:text-slate-100">
                              ♥ {ct.likes.toLocaleString('it-IT')}
                            </span>
                            {!!ct.comments && (
                              <span className="text-slate-500 dark:text-slate-400">
                                {ct.comments.toLocaleString('it-IT')} commenti
                              </span>
                            )}
                            {ct.posted_at && (
                              <span className="text-slate-400">{quando(ct.posted_at)}</span>
                            )}
                          </div>

                          {ct.transcript ? (
                            <>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
                                Script del reel
                              </p>
                              <TestoEspandibile testo={ct.transcript} righe={4} />
                            </>
                          ) : (
                            <p className="text-[12px] text-slate-400">Contenuto visivo, senza parlato.</p>
                          )}

                          {ct.spunto_alessandra && (
                            <Spunto
                              etichetta={`Da riproporre per ${clientName}:`}
                              testo={ct.spunto_alessandra}
                            />
                          )}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default NewsCompetitor;
